// Address formatting utilities for Znoospace

/**
 * Format a Solana address for username display
 * Uses first 3 characters + "..." + last 4 characters
 * @param {string} address - Full Solana address
 * @returns {string} Formatted username
 */
export const formatAddressAsUsername = (address) => {
  if (!address || address.length < 8) {
    return address;
  }
  return `${address.slice(0, 3)}...${address.slice(-4)}`;
};

/**
 * Format a Solana address for display (longer version)
 * Uses first 4 characters + "..." + last 4 characters
 * @param {string} address - Full Solana address
 * @returns {string} Formatted address
 */
export const formatAddressForDisplay = (address) => {
  if (!address || address.length < 12) {
    return address;
  }
  return `${address.slice(0, 4)}...${address.slice(-4)}`;
};

/**
 * Generate a unique display name suggestion when there's a collision
 * @param {string} desiredName - The display name the user wants
 * @param {string} userAddress - User's wallet address
 * @returns {string} Suggested unique display name
 */
export const generateUniqueDisplayName = (desiredName, userAddress) => {
  if (!userAddress || userAddress.length < 8) {
    return desiredName;
  }
  
  // Use first 4 characters of address as prefix
  const addressPrefix = userAddress.slice(0, 4);
  return `${addressPrefix}_${desiredName}`;
};

/**
 * Validate if a display name is acceptable
 * @param {string} displayName - Display name to validate
 * @returns {object} Validation result with isValid and message
 */
export const validateDisplayName = (displayName) => {
  if (!displayName || displayName.trim().length === 0) {
    return {
      isValid: false,
      message: 'Display name cannot be empty'
    };
  }
  
  if (displayName.length > 32) {
    return {
      isValid: false,
      message: 'Display name must be 32 characters or less'
    };
  }
  
  // Check for invalid characters (basic validation)
  const invalidChars = /[<>\"'&]/;
  if (invalidChars.test(displayName)) {
    return {
      isValid: false,
      message: 'Display name contains invalid characters'
    };
  }
  
  return {
    isValid: true,
    message: 'Display name is valid'
  };
};

/**
 * Check if an address is a valid Solana address format
 * @param {string} address - Address to validate
 * @returns {boolean} True if valid format
 */
export const isValidSolanaAddress = (address) => {
  if (!address || typeof address !== 'string') {
    return false;
  }
  
  // Basic Solana address validation (base58, 32-44 characters)
  const solanaAddressRegex = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
  return solanaAddressRegex.test(address);
};
